import { concat, from, of } from 'rxjs';
import { EnrollTwoFactorRequest, Error as MultipassError } from '@square/dashboard-protos/models/squareup/multipass/external';
import { SmsTwoFactor, TwoFactorDetails } from '@square/dashboard-protos/models/squareup/multipass/common';

// These functions must be provided by the parent workflow

// State
// =============================================================================

export const INITIAL_STATE = {
  phoneNumber: '',
  phoneNumberError: '',
  verificationCode: '',
  verificationCodeError: '',
  toggleButtonLoading: false,
  enrollmentComplete: false
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["IncrementNavigation"] = "TWO_FACTOR/INCREMENT_NAVIGATION";
  ActionType["UserChangePhoneNumber"] = "TWO_FACTOR/USER_CHANGE_PHONE_NUMBER";
  ActionType["UserChangeVerificationCode"] = "TWO_FACTOR/USER_CHANGE_VERIFICATION_CODE";
  ActionType["UserClickEnroll"] = "TWO_FACTOR/USER_CLICK_ENROLL";
  ActionType["UserClickVerify"] = "TWO_FACTOR/USER_CLICK_VERIFY";
  ActionType["UserClickResendCode"] = "TWO_FACTOR/USER_CLICK_RESEND_CODE";
  ActionType["PhoneNumberEnrollTwoFactorReceived"] = "TWO_FACTOR/PHONE_NUMBER_ENROLL_TWO_FACTOR_RECEIVED";
  ActionType["PhoneNumberEnrollTwoFactorError"] = "TWO_FACTOR/PHONE_NUMBER_ENROLL_TWO_FACTOR_ERROR";
  ActionType["VerifyCodeEnrollTwoFactorReceived"] = "TWO_FACTOR/VERIFY_CODE_ENROLL_TWO_FACTOR_RECEIVED";
  ActionType["VerifyCodeEnrollTwoFactorError"] = "TWO_FACTOR/VERIFY_CODE_ENROLL_TWO_FACTOR_ERROR";
  ActionType["ToggleButtonLoading"] = "TWO_FACTOR/TOGGLE_BUTTON_LOADING";
  return ActionType;
}({});
// Action Creators
// =============================================================================
export const incrementNavigationAction = () => ({
  type: ActionType.IncrementNavigation
});
export const userChangePhoneNumberAction = phoneNumber => ({
  type: ActionType.UserChangePhoneNumber,
  phoneNumber
});
export const userChangeVerificationCodeAction = verificationCode => ({
  type: ActionType.UserChangeVerificationCode,
  verificationCode
});
export const userClickEnrollAction = () => ({
  type: ActionType.UserClickEnroll
});
export const userClickVerifyAction = () => ({
  type: ActionType.UserClickVerify
});
export const userClickResendCodeAction = () => ({
  type: ActionType.UserClickResendCode
});
export const phoneNumberEnrollTwoFactorReceivedAction = response => ({
  type: ActionType.PhoneNumberEnrollTwoFactorReceived,
  response
});
export const phoneNumberEnrollTwoFactorErrorAction = () => ({
  type: ActionType.PhoneNumberEnrollTwoFactorError
});
export const verifyCodeEnrollTwoFactorReceivedAction = response => ({
  type: ActionType.VerifyCodeEnrollTwoFactorReceived,
  response
});
export const verifyCodeEnrollTwoFactorErrorAction = () => ({
  type: ActionType.VerifyCodeEnrollTwoFactorError
});
export const toggleButtonLoadingAction = value => ({
  type: ActionType.ToggleButtonLoading,
  value
});

// Misc
// =============================================================================

const createEnrollTwoFactorResponse = (multipass, idempotenceKey, phone, verificationCode) => {
  const twoFactorDetails = TwoFactorDetails.create({
    sms: SmsTwoFactor.create({
      phone,
      verificationCode
    })
  });
  return multipass.enrollTwoFactor(EnrollTwoFactorRequest.create({
    twoFactorDetails,
    idempotenceKey
  }));
};

// Update
// =============================================================================

function handleUserChangePhoneNumber(state, action) {
  const {
    phoneNumber
  } = action;
  return [{
    ...state,
    phoneNumber,
    phoneNumberError: ''
  }, null];
}
function handleUserChangeVerificationCode(state, action) {
  const {
    verificationCode
  } = action;
  return [{
    ...state,
    verificationCode,
    verificationCodeError: ''
  }, null];
}
function handleUserClickEnroll(state, context) {
  if (!state.phoneNumber) {
    const phoneNumberError = context.translate('banking.checking.onboarding.twoFactor.phoneNumber.validation.invalidNumber');
    return [{
      ...state,
      phoneNumberError
    }, null];
  }
  const idempotenceKey = context.getUUIDString();
  const work = () => {
    const enrollTwoFactorResponse = createEnrollTwoFactorResponse(context.multipass, idempotenceKey, state.phoneNumber).then(phoneNumberEnrollTwoFactorReceivedAction, phoneNumberEnrollTwoFactorErrorAction);
    return concat(of(toggleButtonLoadingAction(true)), from(enrollTwoFactorResponse));
  };
  return [{
    ...state,
    phoneNumberError: ''
  }, work];
}
function handleUserClickVerify(state, context) {
  if (!state.verificationCode || state.verificationCode.length < 6) {
    const verificationCodeError = context.translate('banking.checking.onboarding.twoFactor.verifyCode.validation.invalidCode');
    return [{
      ...state,
      verificationCodeError
    }, null];
  }
  const idempotenceKey = context.getUUIDString();
  const work = () => {
    const enrollTwoFactorResponse = createEnrollTwoFactorResponse(context.multipass, idempotenceKey, state.phoneNumber, state.verificationCode).then(verifyCodeEnrollTwoFactorReceivedAction, verifyCodeEnrollTwoFactorErrorAction);
    return concat(of(toggleButtonLoadingAction(true)), from(enrollTwoFactorResponse));
  };
  return [{
    ...state,
    verificationCodeError: ''
  }, work];
}
function handleUserClickResendCode(state, context) {
  const idempotenceKey = context.getUUIDString();
  createEnrollTwoFactorResponse(context.multipass, idempotenceKey, state.phoneNumber);
  return [state, null];
}
function handlePhoneNumberEnrollTwoFactorReceived(state, action, context) {
  const {
    response
  } = action;
  const {
    error
  } = response;
  if (error) {
    const phoneNumberError = error === MultipassError.INVALID_ALIAS ? context.translate('banking.checking.onboarding.twoFactor.phoneNumber.validation.invalidNumber') : context.translate('banking.checking.onboarding.twoFactor.error.server');
    const nextState = {
      ...state,
      phoneNumberError
    };
    return [nextState, () => toggleButtonLoadingAction(false)];
  }
  return [{
    ...state,
    verificationCodeError: ''
  }, () => of(toggleButtonLoadingAction(false), incrementNavigationAction())];
}
function handlePhoneNumberEnrollTwoFactorError(state, context) {
  const nextState = {
    ...state,
    phoneNumberError: context.translate('banking.checking.onboarding.twoFactor.error.server')
  };
  return [nextState, () => toggleButtonLoadingAction(false)];
}
function handleVerifyCodeEnrollTwoFactorReceived(state, action, context) {
  const {
    response
  } = action;
  const {
    error
  } = response;
  if (error) {
    const verificationCodeError = error === MultipassError.INVALID_CREDENTIALS ? context.translate('banking.checking.onboarding.twoFactor.verifyCode.validation.invalidCode') : context.translate('banking.checking.onboarding.twoFactor.error.server');
    const nextState = {
      ...state,
      verificationCodeError
    };
    return [nextState, () => toggleButtonLoadingAction(false)];
  }
  return [{
    ...state,
    enrollmentComplete: true
  }, () => of(toggleButtonLoadingAction(false), incrementNavigationAction())];
}
function handleIncrementNavigation(state) {
  // Do not return an action from this handler. The purpose of this action
  // is to signal the parent workflow when navigation should be incremented.
  // This allows the two-factor workflow to be reused without assuming how navigation is handled in the parent workflows.
  return [state, null];
}
function handleVerifyCodeEnrollTwoFactorError(state, context) {
  const nextState = {
    ...state,
    verificationCodeError: context.translate('banking.checking.onboarding.twoFactor.error.server')
  };
  return [nextState, () => toggleButtonLoadingAction(false)];
}
function handleToggleButtonLoading(state, action) {
  const toggleButtonLoading = action.value ?? !state.toggleButtonLoading;
  return [{
    ...state,
    toggleButtonLoading
  }, null];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.IncrementNavigation:
      return handleIncrementNavigation(state);
    case ActionType.UserChangePhoneNumber:
      return handleUserChangePhoneNumber(state, action);
    case ActionType.UserChangeVerificationCode:
      return handleUserChangeVerificationCode(state, action);
    case ActionType.UserClickEnroll:
      return handleUserClickEnroll(state, context);
    case ActionType.UserClickVerify:
      return handleUserClickVerify(state, context);
    case ActionType.UserClickResendCode:
      return handleUserClickResendCode(state, context);
    case ActionType.PhoneNumberEnrollTwoFactorReceived:
      return handlePhoneNumberEnrollTwoFactorReceived(state, action, context);
    case ActionType.PhoneNumberEnrollTwoFactorError:
      return handlePhoneNumberEnrollTwoFactorError(state, context);
    case ActionType.VerifyCodeEnrollTwoFactorReceived:
      return handleVerifyCodeEnrollTwoFactorReceived(state, action, context);
    case ActionType.VerifyCodeEnrollTwoFactorError:
      return handleVerifyCodeEnrollTwoFactorError(state, context);
    case ActionType.ToggleButtonLoading:
      return handleToggleButtonLoading(state, action);
  }
};